import { createContext, useContext, useReducer, useMemo, type ReactNode, type Dispatch } from "react";
import { DURATIONS } from "@/data/categories";
import type { Duration, TimeSlot, OrgUser, TeamMember } from "@/types";

// ─── State ───
export interface BookingState {
  sel: string[];
  dur: Duration | null;
  tentFree: boolean;
  search: string;
  chosen: TimeSlot | null;
  title: string;
  candidate: string;
  candidateEmail: string;
  position: string;
  notes: string;
  videoPlatform: string;
  customVideoLink: string;
  sentEmails: string[];
  copied: boolean;
  syncing: boolean;
  previewTab: string;
  previewPerson: TeamMember | null;
  interviewerTpl: string;
  candidateTpl: string;
  orgUsers: OrgUser[];
}

export const bookingInitialState: BookingState = {
  sel: [],
  dur: null,
  tentFree: false,
  search: "",
  chosen: null,
  title: "",
  candidate: "",
  candidateEmail: "",
  position: "",
  notes: "",
  videoPlatform: "teams",
  customVideoLink: "",
  sentEmails: [],
  copied: false,
  syncing: false,
  previewTab: "participant",
  previewPerson: null,
  interviewerTpl: "",
  candidateTpl: "",
  orgUsers: [],
};

// ─── Actions ───
export type BookingAction =
  | { type: "TOGGLE_MEMBER"; id: string }
  | { type: "SET_MEMBERS"; ids: string[] }
  | { type: "SET_DURATION"; dur: Duration }
  | { type: "SET_TENT_FREE"; value: boolean }
  | { type: "SET_SEARCH"; value: string }
  | { type: "BOOK_SLOT"; slot: TimeSlot }
  | { type: "SET_FIELD"; field: keyof BookingState; value: unknown }
  | { type: "CONFIRM_BOOKING"; emails: string[] }
  | { type: "FINISH_SYNC" }
  | { type: "SET_ORG_USERS"; users: OrgUser[] }
  | { type: "APPLY_SUBCATEGORY_DEFAULTS"; defaultDur?: number; ids?: string[] }
  | { type: "APPLY_NLP"; updates: Partial<BookingState> }
  | { type: "BOOKING_RESET" };

export function bookingReducer(state: BookingState, action: BookingAction): BookingState {
  switch (action.type) {
    case "TOGGLE_MEMBER":
      return {
        ...state,
        sel: state.sel.includes(action.id)
          ? state.sel.filter((x) => x !== action.id)
          : [...state.sel, action.id],
      };

    case "SET_MEMBERS":
      return { ...state, sel: action.ids };

    case "SET_DURATION":
      return { ...state, dur: action.dur };

    case "SET_TENT_FREE":
      return { ...state, tentFree: action.value };

    case "SET_SEARCH":
      return { ...state, search: action.value };

    case "BOOK_SLOT":
      return { ...state, chosen: action.slot };

    case "SET_FIELD":
      return { ...state, [action.field]: action.value };

    case "CONFIRM_BOOKING":
      return { ...state, sentEmails: action.emails, syncing: true };

    case "FINISH_SYNC":
      return { ...state, syncing: false };

    case "SET_ORG_USERS":
      return { ...state, orgUsers: action.users };

    case "APPLY_SUBCATEGORY_DEFAULTS": {
      const selectedIds = action.ids?.length ? action.ids : state.sel;
      const nextDur = action.defaultDur
        ? (DURATIONS.find((d) => d.mins === action.defaultDur) || state.dur)
        : state.dur;
      return { ...state, sel: selectedIds, dur: nextDur };
    }

    case "APPLY_NLP":
      return { ...state, ...action.updates };

    case "BOOKING_RESET":
      return { ...bookingInitialState, orgUsers: state.orgUsers };

    default:
      return state;
  }
}

// ─── Context ───
interface BookingContextValue {
  booking: BookingState;
  bookingDispatch: Dispatch<BookingAction>;
  isInterview: boolean;
}

const BookingContext = createContext<BookingContextValue | null>(null);

/**
 * BookingProvider requires a `category` prop from NavigationContext
 * to compute `isInterview` without coupling the two contexts.
 */
export function BookingProvider({ category, children }: { category: string | null; children: ReactNode }) {
  const [booking, bookingDispatch] = useReducer(bookingReducer, bookingInitialState);
  const isInterview = category === "interview";
  const value = useMemo(() => ({ booking, bookingDispatch, isInterview }), [booking, isInterview]);
  return <BookingContext.Provider value={value}>{children}</BookingContext.Provider>;
}

export function useBooking() {
  const ctx = useContext(BookingContext);
  if (!ctx) throw new Error("useBooking must be used within BookingProvider");
  return ctx;
}
